import { tokenManager } from "../utils/tokenManager";
import { userTimezoneHeaders } from "../utils/userTimezoneHeader";

/**
 * API base URL configuration
 */
export const API_BASE_URL = process.env.REACT_APP_API_URL || "";

// const API_BASE_URL ='http://localhost:3000/api'

/**
 * Get authentication token
 */
const getAuthToken = () => {
  const token = tokenManager.getToken();
  console.log("Getting auth token:", { hasToken: !!token });
  return token;
};

/**
 * Generic API request function
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Request options
 * @returns {Promise<Object>} API response
 */
export const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const { headers: optionHeaders = {}, ...restOptions } = options;

  /**
   * Merge `options` without letting `options.headers` replace the whole header
   * object (which would drop Authorization and break POST bodies in DevTools).
   */
  const config = {
    ...restOptions,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...userTimezoneHeaders(),
      ...optionHeaders,
    },
  };

  const fullUrl = `${API_BASE_URL}${endpoint}`;
  console.log("Making API request:", {
    url: fullUrl,
    endpoint,
    hasAuthHeader: !!config.headers.Authorization,
  });

  try {
    const response = await fetch(fullUrl, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("API request failed:", {
        status: response.status,
        error: errorData,
      });
      if (errorData.loggedIn === false) {
        tokenManager.clearAuth();
        window.location.href = "/login";
        return;
      }
      // Create a custom error object that preserves the full API response
      const error = new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
      error.status = response.status;
      error.data = errorData;
      error.errors = errorData.errors || [];
      throw error;
    }

    return await response.json();
  } catch (error) {
    console.error("API request failed:", error);
    throw error;
  }
};

/**
 * HTTP method wrapper functions
 */
export const get = async (endpoint, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiRequest(`${endpoint}?${queryString}`, { method: "GET" });
};
export const getWithoutQueryString = async (endpoint) => {
  return apiRequest(endpoint, { method: "GET" });
};
export const post = async (endpoint, data) => {
  return apiRequest(endpoint, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const put = async (endpoint, data) => {
  return apiRequest(endpoint, {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

export const del = async (endpoint) => {
  return apiRequest(endpoint, { method: "DELETE" });
};

/**
 * User API endpoints
 */

export const userAPI = {
  /**
   * Get all users with pagination and search
   */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await apiRequest(`/users?${queryString}`);
    return response.data; // Extract data from response
  },

  /**
   * Get user by ID
   */
  getById: async (id) => {
    const response = await apiRequest(`/users/${id}`);
    return response.data;
  },

  /**
   * Create new user
   */
  create: async (userData) => {
    const response = await apiRequest("/users", {
      method: "POST",
      body: JSON.stringify(userData),
    });
    return response.data;
  },

  /**
   * Update user
   */
  update: async (id, userData) => {
    const response = await apiRequest(`/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(userData),
    });
    return response.data;
  },

  /**
   * Delete user
   */
  delete: async (id) => {
    return apiRequest(`/users/${id}`, {
      method: "DELETE",
    });
  },

  /**
   * Get user dependencies (sessions, etc.)
   */
  getDependencies: async (id) => {
    const response = await apiRequest(`/users/${id}/dependencies`);
    return response.data;
  },

  /**
   * Download users as CSV
   */
  downloadUsers: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_BASE_URL}/users/download?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "text/csv",
          ...(getAuthToken() && { Authorization: `Bearer ${getAuthToken()}` }),
          ...userTimezoneHeaders(),
        },
      },
    );
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users_export_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    return { filename: a.download };
  },
};

export const siteRecordingsAPI = {
  /**
   * Get all siteRecordings with pagination, filtering, and sorting
   *
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (1-indexed, default: 1)
   * @param {number} params.limit - Items per page (default: 50)
   * @param {string} params.search - Search query (searches in filename and locationName)
   * @param {string} params.filterType - Filter by upload status: 'all' | 'uploaded' | 'not-uploaded'
   * @param {number} params.session_id - Filter by session ID
   * @param {string} params.drawingName - Filter by drawing name (without extension)
   * @param {string} params.dateFilter - Date filter option: 'today' | 'yesterday' | 'thisWeek' | 'thisMonth' | 'custom'
   * @param {string} params.startDate - Start date (ISO string, required if dateFilter is 'custom')
   * @param {string} params.endDate - End date (ISO string, required if dateFilter is 'custom')
   * @param {string} params.sortField - Sort field: 'name' | 'location' | 'timestamp' | 'duration' | 'size' | 'session'
   * @param {string} params.sortDirection - Sort direction: 'asc' | 'desc'
   *
   * @returns {Promise<Object>} Response object with:
   *   - recordings: Array of recording objects
   *   - total: Total count of records matching filters
   *   - page: Current page number
   *   - limit: Items per page
   *   - totalPages: Total number of pages
   */
  getAll: async (params = {}) => {
    // Build query string, handling nested objects (like dateFilter)
    const queryParams = new URLSearchParams();

    // Pagination
    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);

    // Search
    if (params.search) queryParams.append("search", params.search);

    // Filters
    if (params.filterType && params.filterType !== "all") {
      queryParams.append("filterType", params.filterType);
    }
    if (params.session_id && params.session_id !== "all") {
      queryParams.append("session_id", params.session_id);
    }
    if (params.drawingName) {
      queryParams.append("drawingName", params.drawingName);
    }
    if (params.drawingId) {
      queryParams.append("drawingId", params.drawingId);
    }

    // Date filter
    if (params.dateFilter) {
      queryParams.append("dateFilter", params.dateFilter);
    }
    if (params.startDate) {
      queryParams.append("startDate", params.startDate);
    }
    if (params.endDate) {
      queryParams.append("endDate", params.endDate);
    }

    // Sorting
    if (params.sortField) {
      queryParams.append("sortField", params.sortField);
    }
    if (params.sortDirection) {
      queryParams.append("sortDirection", params.sortDirection);
    }

    const queryString = queryParams.toString();
    const response = await apiRequest(
      `/timerRecordings/recordings?${queryString}`,
    );

    // Backend should return: { recordings: [...], total: number, page: number, limit: number, totalPages: number }
    // If backend returns array directly, wrap it for compatibility
    if (Array.isArray(response)) {
      return {
        recordings: response,
        total: response.length,
        page: params.page || 1,
        limit: params.limit || 20,
        totalPages: Math.ceil(response.length / (params.limit || 20)),
      };
    }

    // Return response as-is if it's already an object
    return response;
  },

  delete: async (id) => {
    return apiRequest(`/timerRecordings/deleteRecording/${id}`, {
      method: "DELETE",
    });
  },
};

/**
 * Pins API endpoints
 */
export const pinsAPI = {
  /**
   * Get pins calendar data for a specific month and year
   * @param {number} month - Month number (1-12)
   * @param {number} year - Year (e.g., 2024)
   * @returns {Promise<Object>} API response with calendar data
   */
  getCalendar: async (month, year) => {
    const queryParams = new URLSearchParams({
      month: month.toString(),
      year: year.toString(),
    });
    const response = await apiRequest(
      `/pins/calendar?${queryParams.toString()}`,
    );
    return response;
  },
};

/**
 * IoT Device API endpoints - Commented Out
 */
// export const iotDeviceAPI = {
//   /**
//    * Get all IoT devices with pagination and filters
//    */
//   getAll: async (params = {}) => {
//     const queryString = new URLSearchParams(params).toString();
//     const response = await apiRequest(`/iot-devices?${queryString}`);
//     return response.data;
//   },

//   /**
//    * Get IoT device by ID
//    */
//   getById: async (id) => {
//     const response = await apiRequest(`/iot-devices/${id}`);
//     return response.data;
//   },

//   /**
//    * Get IoT device by UID
//    */
//   getByUID: async (uid) => {
//     const response = await apiRequest(`/iot-devices/uid/${uid}`);
//     return response.data;
//   },

//   /**
//    * Create new IoT device
//    */
//   create: async (deviceData) => {
//     const response = await apiRequest('/iot-devices', {
//       method: 'POST',
//       body: JSON.stringify(deviceData),
//     });
//     return response.data;
//   },

//   /**
//    * Update IoT device
//    */
//   update: async (id, deviceData) => {
//     const response = await apiRequest(`/iot-devices/${id}`, {
//       method: 'PUT',
//       body: JSON.stringify(deviceData),
//     });
//     return response.data;
//   },

//   /**
//    * Delete IoT device
//    */
//   delete: async (id) => {
//     return apiRequest(`/iot-devices/${id}`, {
//       method: 'DELETE',
//     });
//   },

//   /**
//    * Update IoT device health status
//    */
//   updateHealth: async (id, healthStatus) => {
//     const response = await apiRequest(`/iot-devices/${id}/health`, {
//       method: 'PATCH',
//       body: JSON.stringify({ health_status: healthStatus }),
//     });
//     return response.data;
//   },

//   /**
//    * Update IoT device status
//    */
//   updateStatus: async (id, status) => {
//     const response = await apiRequest(`/iot-devices/${id}/status`, {
//       method: 'PATCH',
//       body: JSON.stringify({ status }),
//     });
//     return response.data;
//   },

//   /**
//    * Bulk update device status
//    */
//   bulkUpdateStatus: async (deviceIds, status) => {
//     const response = await apiRequest('/iot-devices/bulk/status', {
//       method: 'PATCH',
//       body: JSON.stringify({ device_ids: deviceIds, status }),
//     });
//     return response.data;
//   },

//   /**
//    * Get IoT device statistics
//    */
//   getStats: async () => {
//     const response = await apiRequest('/iot-devices/stats');
//     return response.data;
//   },
// };

/**
 * Dashboard API endpoints
 */
export const dashboardAPI = {
  /**
   * Get dashboard statistics
   * @param {Object} params - Query parameters
   * @param {string} params.period - Time period (e.g., 'last_7_days', 'last_30_days')
   * @param {number} params.use_case_id - Use case ID (optional)
   * @param {number} params.source_id - Source ID (optional)
   * @returns {Promise<Object>} Dashboard statistics
   */
  getStatistics: async (params = {}) => {
    const queryParams = new URLSearchParams();

    if (params.period) {
      queryParams.append("period", params.period);
    }
    if (params.use_case_id) {
      queryParams.append("use_case_id", params.use_case_id);
    }
    if (params.source_id) {
      queryParams.append("source_id", params.source_id);
    }

    const queryString = queryParams.toString();
    const endpoint = `/alerts/dashboard/statistics${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, { method: "GET" });
    return response;
  },
};

/**
 * Alert History API endpoints
 */
export const alertsHistoryAPI = {
  /**
   * Get alert history with filters
   * @param {Object} params - Query parameters
   * @param {string} params.search - Search query for filtering alerts (optional)
   * @param {string} params.alert_name - Filter by alert name (partial match, case-insensitive) (optional, deprecated - use search)
   * @param {number} params.limit - Number of results to return (deprecated, use per_page)
   * @param {number} params.offset - Number of results to skip for pagination (deprecated, use page)
   * @param {number} params.page - Page number for pagination (optional, default: 1)
   * @param {number} params.page_size - Page size (1–200); preferred for GET /alerts/history
   * @param {number} params.per_page - Alias for page_size (optional)
   * @param {string} params.period - Time period filter (optional)
   * @param {string} params.start_date - Start￼ date in YYYY-MM-DD format (optional)
   * @param {string} params.end_date - End date in YYYY-MM-DD format (optional)
   * @param {string|number|Array} params.source_id - Filter by source ID(s) - can be single value, comma-separated string, or array (optional)
   * @param {string|Array} params.severity - Filter by severity level(s): low, medium, high - can be single value, comma-separated string, or array (optional)
   * @param {string|number|Array} params.rule_id - Filter by rule ID(s) - can be single value, comma-separated string, or array (optional)
   * @param {string} params.media_type - Filter by media type: photo or video (optional)
   * @param {string} [params.sort_by] - Sort field: `created_at` (default) or `severity`
   * @param {string} [params.sort_order] - `asc` or `desc` (default `desc`)
   * @returns {Promise<Object>} Alert history data
   */
  getHistory: async (params = {}) => {
    const queryParams = new URLSearchParams();
    const appendCsvQueryParam = (key, value) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        const vals = value
          .map((v) => String(v ?? "").trim())
          .filter(Boolean);
        if (vals.length > 0) queryParams.append(key, vals.join(","));
        return;
      }
      const s = String(value).trim();
      if (s) queryParams.append(key, s);
    };

    // Use search parameter (preferred) or fallback to alert_name for backward compatibility
    if (params.search) {
      queryParams.append("search", params.search);
    } else if (params.alert_name) {
      queryParams.append("alert_name", params.alert_name);
    }
    // Support both limit and per_page - API accepts limit
    const pageSizeForQuery =
      params.page_size ?? params.per_page ?? params.limit ?? null;
    if (pageSizeForQuery != null && String(pageSizeForQuery).trim() !== "") {
      queryParams.append("page_size", String(pageSizeForQuery));
    }
    if (params.page) {
      queryParams.append("page", params.page);
    }
    // Support legacy offset for backward compatibility
    if (params.offset && !params.page) {
      const ps = Number(pageSizeForQuery) || 20;
      const page = Math.floor(params.offset / ps) + 1;
      queryParams.append("page", page);
    }
    if (params.period) {
      queryParams.append("period", params.period);
    }
    if (params.start_date) {
      queryParams.append("start_date", params.start_date);
    }
    if (params.end_date) {
      queryParams.append("end_date", params.end_date);
    }
    appendCsvQueryParam("source_id", params.source_id);
    appendCsvQueryParam("source_name", params.source_name);
    appendCsvQueryParam("rule_id", params.rule_id);
    appendCsvQueryParam("severity", params.severity);
    appendCsvQueryParam("status", params.status);
    appendCsvQueryParam("notification_status", params.notification_status);
    appendCsvQueryParam("notify_by", params.notify_by);
    appendCsvQueryParam("alert_type", params.alert_type);
    appendCsvQueryParam("roi_id", params.roi_id);

    if (params.media_type) {
      queryParams.append("media_type", params.media_type);
    }
    const sortBy =
      params.sort_by != null && String(params.sort_by).trim() !== ""
        ? String(params.sort_by).trim()
        : "created_at";
    queryParams.append("sort_by", sortBy);
    const sortOrderRaw = String(params.sort_order ?? "desc")
      .trim()
      .toLowerCase();
    queryParams.append(
      "sort_order",
      sortOrderRaw === "asc" ? "asc" : "desc",
    );

    const queryString = queryParams.toString();
    const endpoint = `/alerts/history${queryString ? `?${queryString}` : ""}`;
    const response = await apiRequest(endpoint, { method: "GET" });
    return response;
  },

  /**
   * PATCH single alert workspace status (query param), e.g. acknowledged / closed / active.
   * Matches backend: PATCH /alert/{id}?status=...
   * Uses fetch directly so empty JSON bodies do not break parsing.
   */
  patchAlertStatus: async (alertId, status) => {
    const id = String(alertId ?? "").trim();
    if (!id) {
      throw new Error("Alert id is required");
    }
    const token = getAuthToken();
    const query = new URLSearchParams({
      status: String(status ?? "").trim().toLowerCase(),
    });
    const endpoint = `/alert/${encodeURIComponent(id)}?${query}`;
    const fullUrl = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(fullUrl, {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...userTimezoneHeaders(),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.loggedIn === false) {
        tokenManager.clearAuth();
        window.location.href = "/login";
        return null;
      }
      const error = new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
      error.status = response.status;
      error.data = errorData;
      error.errors = errorData.errors || [];
      throw error;
    }

    const text = await response.text();
    if (!text.trim()) return {};
    try {
      return JSON.parse(text);
    } catch {
      return {};
    }
  },
};

/**
 * Triggers API endpoints
 */
export const triggersAPI = {
  /**
   * List triggers with pagination and filtering
   * @param {Object} params - Query parameters (optional)
   * @param {number} [params.page] - Page number for pagination (optional, default: 1)
   * @param {number} [params.per_page] - Number of triggers per page (optional, default: 20)
   * @param {string} [params.action] - Filter by action type (e.g., "EMAIL", "SMS", "HTTP") (optional)
   * @param {string} [params.event] - Filter by event type (optional)
   * @returns {Promise<Object>} List of triggers with pagination metadata
   */
  list: async (params = {}) => {
    const queryParams = new URLSearchParams();

    if (params.page) {
      queryParams.append("page", params.page);
    }
    if (params.per_page) {
      queryParams.append("per_page", params.per_page);
    }
    if (params.action) {
      queryParams.append("action", params.action);
    }
    if (params.event) {
      queryParams.append("event", params.event);
    }

    const queryString = queryParams.toString();
    const endpoint = `/triggers${queryString ? `?${queryString}` : ""}`;
    const response = await apiRequest(endpoint, { method: "GET" });
    return response;
  },

  /**
   * Get trigger by ID
   * @param {number} triggerId - Trigger ID
   * @returns {Promise<Object>} Trigger data
   */
  get: async (triggerId) => {
    const endpoint = `/triggers/${triggerId}`;
    const response = await apiRequest(endpoint, { method: "GET" });
    return response;
  },

  /**
   * Get triggers by rule ID
   * @param {number} ruleId - Rule ID
   * @returns {Promise<Object>} Triggers data for the rule
   */
  getByRuleId: async (ruleId) => {
    const endpoint = `/triggers/rule/${ruleId}`;
    const response = await apiRequest(endpoint, { method: "GET" });
    return response;
  },

  /**
   * Update an existing trigger
   * @param {number} triggerId - Trigger ID
   * @param {Object} triggerData - Updated trigger data (partial update supported)
   * @returns {Promise<Object>} Updated trigger data
   */
  update: async (triggerId, triggerData) => {
    const endpoint = `/triggers/${triggerId}`;
    try {
      const response = await apiRequest(endpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(triggerData),
      });
      return response;
    } catch (error) {
      // Log detailed error information for 422 errors
      if (error.status === 422) {
        console.error("Validation Error (422):", {
          message: error.message,
          errors: error.errors,
          data: error.data,
          requestBody: triggerData,
        });
      }
      throw error;
    }
  },

  /**
   * Create a new trigger
   * @param {Object} triggerData - Trigger data
   * @param {string} triggerData.name - Trigger name
   * @param {string} [triggerData.event] - Event type (e.g., "SECURITY_ALERT") when required by API
   * @param {string} triggerData.action - Action type (e.g., "EMAIL", "SMS", "WEBHOOK")
   * @param {string} [triggerData.notified_to] - Comma-separated display names (Notified to)
   * @param {Object} triggerData.config - Configuration object
   * @param {Array<string>} triggerData.config.recipients - Email addresses for EMAIL action
   * @param {Array<string>} triggerData.config.cc - CC email addresses (optional)
   * @param {Array<string>} triggerData.config.bcc - BCC email addresses (optional)
   * @param {string} triggerData.config.subject - Email subject (for EMAIL action)
   * @param {string} triggerData.config.message_body - Message body
   * @returns {Promise<Object>} Created trigger data
   */
  create: async (triggerData) => {
    const endpoint = `/triggers/`;
    try {
      const response = await apiRequest(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(triggerData),
      });
      return response;
    } catch (error) {
      // Log detailed error information for 422 errors
      if (error.status === 422) {
        console.error("Validation Error (422):", {
          message: error.message,
          errors: error.errors,
          data: error.data,
          requestBody: triggerData,
        });
      }
      throw error;
    }
  },

  /**
   * Delete a trigger by ID
   * @param {number} triggerId - Trigger ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  delete: async (triggerId) => {
    const endpoint = `/triggers/${triggerId}`;
    try {
      const response = await apiRequest(endpoint, { method: "DELETE" });
      return response;
    } catch (error) {
      console.error("Error deleting trigger:", {
        status: error.status,
        message: error.message,
        errors: error.errors,
        data: error.data,
      });
      throw error;
    }
  },
};

/**
 * Notifications API — catalog for trigger message_body template placeholders.
 */
export const notificationsAPI = {
  /**
   * List placeholder keys supported in EMAIL / TELEGRAM / SMS trigger message_body templates.
   * @returns {Promise<{ success?: boolean; message?: string; data?: { template_style?: string; placeholder_syntax?: string; applies_to?: string[]; placeholders?: Array<{ key: string; label: string; category: string; value_kind: string }> } }>}
   */
  getMessagePlaceholders: async () => {
    return getWithoutQueryString("/notifications/message-placeholders");
  },
};

/**
 * AI alert service base (use cases, cameras, zones).
 * Prefer REACT_APP_API_URL_FOR_AI_DETECTION; fall back to REACT_APP_API_URL.
 */
function getAiAlertServiceBaseUrl() {
  const raw =
    process.env.REACT_APP_API_URL_FOR_AI_DETECTION ||
    process.env.REACT_APP_API_URL ||
    "";
  return String(raw).trim().replace(/\/$/, "");
}

/**
 * Use cases API (GET /use-cases/) — list models for Alert Type in Add Rule.
 */
export const useCasesAPI = {
  /**
   * @param {{ user_id?: string|null }} [params] - Omit user_id for admin list; set to filter by assignment.
   * @returns {Promise<Object>} Raw JSON (e.g. { message, status, data: [...] }).
   */
  list: async (params = {}) => {
    const base = getAiAlertServiceBaseUrl();
    if (!base) {
      const err = new Error(
        "API URL is not configured. Set REACT_APP_API_URL or REACT_APP_API_URL_FOR_AI_DETECTION.",
      );
      err.code = "MISSING_USE_CASES_BASE_URL";
      throw err;
    }
    const query = new URLSearchParams();
    if (
      params.user_id != null &&
      String(params.user_id).trim() !== "" &&
      params.user_id !== "null"
    ) {
      query.set("user_id", String(params.user_id).trim());
    }
    const qs = query.toString();
    const url = `${base}/use-cases/${qs ? `?${qs}` : ""}`;
    const token = getAuthToken();
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...userTimezoneHeaders(),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("useCasesAPI.list failed:", {
        status: response.status,
        error: errorData,
        url,
      });
      if (errorData.loggedIn === false) {
        tokenManager.clearAuth();
        window.location.href = "/login";
        return;
      }
      const error = new Error(
        errorData.message ||
          errorData.detail ||
          `HTTP error! status: ${response.status}`,
      );
      error.status = response.status;
      error.data = errorData;
      throw error;
    }

    return response.json();
  },
};

async function aiAlertServiceFetchJson(pathWithLeadingSlash, options = {}) {
  const base = getAiAlertServiceBaseUrl();
  if (!base) {
    const err = new Error(
      "API URL is not configured. Set REACT_APP_API_URL or REACT_APP_API_URL_FOR_AI_DETECTION.",
    );
    err.code = "MISSING_AI_ALERT_BASE_URL";
    throw err;
  }
  const url = `${base}${pathWithLeadingSlash.startsWith("/") ? pathWithLeadingSlash : `/${pathWithLeadingSlash}`}`;
  const token = getAuthToken();
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...userTimezoneHeaders(),
      ...(options.headers && typeof options.headers === "object"
        ? options.headers
        : {}),
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error("aiAlertServiceFetchJson failed:", {
      status: response.status,
      error: errorData,
      url,
    });
    if (errorData.loggedIn === false) {
      tokenManager.clearAuth();
      window.location.href = "/login";
      return;
    }
    const error = new Error(
      errorData.message ||
        errorData.detail ||
        `HTTP error! status: ${response.status}`,
    );
    error.status = response.status;
    error.data = errorData;
    throw error;
  }

  return response.json();
}

/**
 * Cameras & zones on the AI alert service (GET /cameras, GET /cameras/:id/zones).
 */
export const camerasAPI = {
  /**
   * @param {{ is_active?: boolean }} [params] - Optional filter (omit for all cameras).
   */
  list: async (params = {}) => {
    const query = new URLSearchParams();
    if (params.is_active === true || params.is_active === false) {
      query.set("is_active", String(params.is_active));
    }
    const qs = query.toString();
    const path = `/cameras${qs ? `?${qs}` : ""}`;
    return aiAlertServiceFetchJson(path);
  },

  /**
   * @param {string} cameraId - Camera UUID
   */
  listZones: async (cameraId) => {
    const id = encodeURIComponent(String(cameraId));
    return aiAlertServiceFetchJson(`/cameras/${id}/zones`);
  },
};

/**
 * Zones on the AI alert service (GET /zones).
 */
export const zonesAPI = {
  /**
   * List zones.
   * @returns {Promise<Object>} Raw JSON response (array or { data: [...] }).
   */
  list: async () => {
    return aiAlertServiceFetchJson("/zones");
  },
};

/**
 * Alert Rules API endpoints
 */
export const alertRulesAPI = {
  /**
   * List all rules with optional filtering
   * @param {Object} params - Query parameters (optional)
   * @returns {Promise<Object>} List of rules
   */
  list: async (params = {}) => {
    const queryParams = new URLSearchParams();

    // Add any query parameters if provided
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null) {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    const endpoint = `/rules${queryString ? `?${queryString}` : ""}`;
    const response = await apiRequest(endpoint, { method: "GET" });
    return response;
  },

  /**
   * Get rule by ID
   * @param {number|string} ruleId - Rule ID
   * @returns {Promise<Object>} Rule data
   */
  get: async (ruleId) => {
    const endpoint = `/rules/${ruleId}`;
    const response = await apiRequest(endpoint, { method: "GET" });
    return response;
  },

  /**
   * Update an existing rule
   * @param {number|string} ruleId - Rule ID
   * @param {Object} ruleData - Updated rule data (partial update supported)
   * @param {string} [ruleData.alert_name] - Rule name
   * @param {string} [ruleData.alert_type] - Alert type
   * @returns {Promise<Object>} Updated rule data
   */
  update: async (ruleId, ruleData) => {
    const endpoint = `/rules/${ruleId}`;
    try {
      console.log(
        "Updating rule:",
        ruleId,
        "with data:",
        JSON.stringify(ruleData, null, 2),
      );
      const response = await apiRequest(endpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ruleData),
      });
      return response;
    } catch (error) {
      console.error("Error updating rule:", {
        status: error.status,
        message: error.message,
        errors: error.errors,
        data: error.data,
        requestBody: ruleData,
      });
      throw error;
    }
  },

  /**
   * Create a new alert rule
   * @param {Object} ruleData - Rule data
   * @param {string} ruleData.alert_name - Rule name
   * @param {string} ruleData.source_type - Source type (e.g., "camera")
   * @param {string} ruleData.alert_type - Alert type
   * @param {number} ruleData.source_id - Source ID
   * @param {number} [ruleData.roi_id] - ROI ID (optional)
   * @param {string} ruleData.rule_severity - Severity level (e.g., "high", "medium", "low")
   * @param {Object} ruleData.schedule - Schedule object with days, start_time, end_time
   * @param {number} ruleData.cooldown_interval - Cooldown interval in seconds
   * @param {Array<number>} ruleData.trigger_ids - Array of trigger IDs
   * @returns {Promise<Object>} Created rule data
   */
  create: async (ruleData) => {
    const endpoint = `/rules/`;
    try {
      console.log(
        "Creating rule with data:",
        JSON.stringify(ruleData, null, 2),
      );
      const response = await apiRequest(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ruleData),
      });
      return response;
    } catch (error) {
      // Log detailed error information for all errors
      console.error("Error creating rule:", {
        status: error.status,
        message: error.message,
        errors: error.errors,
        data: error.data,
        requestBody: ruleData,
      });

      if (error.status === 422) {
        console.error("Validation Error (422):", {
          message: error.message,
          errors: error.errors,
          data: error.data,
          requestBody: ruleData,
        });
      } else if (error.status === 500) {
        console.error("Server Error (500):", {
          message: error.message,
          data: error.data,
          requestBody: ruleData,
        });
      }
      throw error;
    }
  },

  /**
   * Delete a rule by ID
   * @param {number|string} ruleId - Rule ID
   * @returns {Promise<Object>} Deletion response
   */
  delete: async (ruleId) => {
    const endpoint = `/rules/${ruleId}`;
    try {
      console.log("Deleting rule:", ruleId);
      const response = await apiRequest(endpoint, {
        method: "DELETE",
      });
      return response;
    } catch (error) {
      console.error("Error deleting rule:", {
        status: error.status,
        message: error.message,
        data: error.data,
      });
      throw error;
    }
  },
};

/**
 * PUT notification-credentials expect a wrapped body:
 * `{ "credentials": { ... } }` (SMS, EMAIL, TELEGRAM).
 * @param {string} _serviceType
 * @param {Object} credentials
 * @returns {string}
 */
function serializeNotificationCredentialsBody(_serviceType, credentials) {
  const creds =
    credentials != null && typeof credentials === "object" && !Array.isArray(credentials)
      ? credentials
      : {};
  return JSON.stringify({ credentials: creds });
}

/**
 * Backend validates `query.user_id` on notification-credentials, user-settings
 * cooldown-range, and alert-rules settings routes.
 * @returns {string|null}
 */
function getAuthUserIdForQuery() {
  const user = tokenManager.getUser();
  if (!user || typeof user !== "object") return null;
  const raw = user.id ?? user.user_id ?? user.userId;
  if (raw == null) return null;
  const s = String(raw).trim();
  if (s === "" || s === "null") return null;
  return s;
}

/** @param {URLSearchParams} queryParams */
function appendUserIdQueryParam(queryParams) {
  const uid = getAuthUserIdForQuery();
  if (uid) queryParams.append("user_id", uid);
}

/**
 * Notification Credentials API endpoints
 */
export const notificationCredentialsAPI = {
  /**
   * Get all notification credentials for the logged-in user.
   * @returns {Promise<Object>} All credentials data
   */
  getAll: async () => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    const queryString = queryParams.toString();
    const endpoint = `/notification-credentials/${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, {
      method: "GET",
    });
    return response;
  },

  /**
   * Get notification credentials for a service type
   * @param {string} serviceType - Service type (TELEGRAM, EMAIL, SMS)
   * @returns {Promise<Object>} Credentials data
   */
  get: async (serviceType) => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    const queryString = queryParams.toString();
    const endpoint = `/notification-credentials/${serviceType}${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, {
      method: "GET",
    });
    return response;
  },

  /**
   * Create or replace notification credentials for a service type (PUT upsert).
   * When a row already exists, omitted credential fields are left unchanged per API contract.
   * @param {string} serviceType - Service type (TELEGRAM, EMAIL, SMS)
   * @param {Object} credentials - Credentials data
   * @returns {Promise<Object>} Upserted credentials data
   */
  update: async (serviceType, credentials) => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    const queryString = queryParams.toString();
    const endpoint = `/notification-credentials/${serviceType}${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: serializeNotificationCredentialsBody(serviceType, credentials),
    });
    return response;
  },

  /**
   * Delete notification credentials for a service type
   * @param {string} serviceType - Service type (TELEGRAM, EMAIL, SMS)
   * @returns {Promise<Object>} Deletion response
   */
  delete: async (serviceType) => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    const queryString = queryParams.toString();
    const endpoint = `/notification-credentials/${serviceType}${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, {
      method: "DELETE",
    });
    return response;
  },
};

/**
 * User Settings API endpoints
 */
export const userSettingsAPI = {
  /**
   * Get cooldown range settings
   * @returns {Promise<Object>} Cooldown range data
   */
  getCooldownRange: async () => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    const queryString = queryParams.toString();
    const endpoint = `/user-settings/cooldown-range${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, {
      method: "GET",
    });
    return response;
  },

  /**
   * Set cooldown range for the user (PUT — create or replace per API contract).
   * @param {Object} cooldownRange - Cooldown range data { min, max }
   * @returns {Promise<Object>} Cooldown range configuration
   */
  updateCooldownRange: async (cooldownRange) => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    const queryString = queryParams.toString();
    const endpoint = `/user-settings/cooldown-range${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cooldownRange),
    });
    return response;
  },
};

/**
 * Alert Rules Settings API endpoints
 */
export const alertRulesSettingsAPI = {
  /**
   * Get alert rules settings
   * @returns {Promise<Object>} Settings data
   */
  get: async () => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    const queryString = queryParams.toString();
    const endpoint = `/alert-rules/settings${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, { method: "GET" });
    return response;
  },

  /**
   * Update alert rules settings
   * @param {Object} settingsData - Settings data to update
   * @returns {Promise<Object>} Updated settings data
   */
  update: async (settingsData) => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    const queryString = queryParams.toString();
    const endpoint = `/alert-rules/settings${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(settingsData),
    });
    return response;
  },

  /**
   * Delete alert rules settings for a specific service type
   * @param {string} serviceType - Service type to delete (TELEGRAM, EMAIL, SMS)
   * @returns {Promise<Object>} Deletion response
   */
  delete: async (serviceType) => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    if (serviceType) {
      queryParams.append("service_type", serviceType);
    }
    const queryString = queryParams.toString();
    const endpoint = `/alert-rules/settings${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, {
      method: "DELETE",
    });
    return response;
  },
};

/**
 * User display settings keyed by module (e.g. patrol list columns, defaults).
 * Stub returns empty data so Patrol falls back to localStorage until a backend route exists.
 */
export const userDisplaySettingsAPI = {
  get: async (_moduleKey) => ({ data: {} }),
  save: async (_moduleKey, _payload) => ({ ok: true }),
};

/**
 * Session media / floor-plan pins for patrol. Stub for dev when construction session API is unavailable.
 */
export const sessionAPI = {
  getMediaPins: async (_sessionId) => ({
    data: {
      path: "patrol/dummy",
      orig_filename: "plan.jpg",
      media_id: "placeholder",
      extension: "jpg",
      pins: [{ id: "dummy-pin-1" }],
    },
  }),
};
